import {Plain} from '../../../../../shared/model/plain.type.model';
import {COMPARISON_WORDING} from '../../model/conditions/comparison-wording.model';
import {CONDITION_OPERATORS} from '../../model/conditions/condition-operators.model';
import {ConditionComparison} from '../../model/conditions/condition-comparison.model';
import {CONDITION_VALUE_INPUT} from '../../model/conditions/condition-value-input.model';
import {DATE_CONDITION_OPERATORS} from '../../model/conditions/date-condition-operators.model';
import {EQUALITY_CONDITION_OPERATORS} from '../../model/conditions/equality-condition-operators.model';
import {NO_COMPARISON} from '../../model/conditions/no-comparison.model';
import {TEXT_CONDITION_OPERATORS} from '../../model/conditions/text-condition-operators.model';
import {MetadataListValue} from './metadata.list.value.model';
import {METADATA_TYPE} from './metadata.type.model';

/**
 * Ce que l'on sait d'un répondant en dehors de ses réponses : son genre, sa langue, son agence, sa
 * date de naissance… Les standard sont livrées avec l'application, les personnalisées sont créées
 * par la société.
 *
 * Une classe et non une simple structure : une métadonnée sait comment une condition d'affichage se
 * compare à elle, et c'est à elle de le dire. Comme toute classe du domaine, elle se réhydrate par
 * son constructeur — l'adapter fait `new Metadata(raw)`, `HttpClient` ne rend que du JSON nu.
 */
export class Metadata {
    public id: number;
    public name: string;
    public type: METADATA_TYPE;
    public attachment: 'CONTACT' | 'RESPONDENT';
    public personalData: boolean;
    public custom: boolean;
    public mandatory: boolean;
    public maxLength?: number;
    /** Libellés affichés, par code langue, avec `default` en repli. */
    public translationNames?: Record<string, string>;
    public companyId?: number;
    public values?: MetadataListValue[];
    public archived?: boolean;

    constructor(data: Plain<Metadata>) {
        this.id = data.id;
        this.name = data.name;
        this.type = data.type;
        this.attachment = data.attachment;
        this.personalData = data.personalData;
        this.custom = data.custom;
        this.mandatory = data.mandatory;
        this.maxLength = data.maxLength;
        this.translationNames = data.translationNames;
        this.companyId = data.companyId;
        this.values = data.values;
        this.archived = data.archived;
    }

    /**
     * Comment une condition d'affichage se compare à cette métadonnée.
     *
     * Quatre familles se distinguent. Ce qui ne s'ordonne pas — le genre, la langue, une liste de
     * valeurs comme l'agence — ne se compare que par égalité, et se choisit dans une liste. Une date
     * se situe dans le temps : « Le », « Avant ou le »…, et se prend dans un calendrier. Un nombre —
     * le chiffre d'affaires — se compare par grandeur et se tape. Un texte libre ne s'ordonne pas
     * mais se fouille : « contient », selon qu'on respecte la casse ou non.
     *
     * Le téléphone et le courriel ne sont pas exploités : ils n'offrent donc rien à saisir.
     */
    public conditionComparison(): ConditionComparison {
        switch (this.type) {
            case METADATA_TYPE.GENDER:
            case METADATA_TYPE.LANGUAGE:
            case METADATA_TYPE.LIST:
                return {
                    operators: EQUALITY_CONDITION_OPERATORS,
                    wording: COMPARISON_WORDING.STATE,
                    input: CONDITION_VALUE_INPUT.LIST,
                };
            case METADATA_TYPE.DATE:
                return {
                    operators: DATE_CONDITION_OPERATORS,
                    wording: COMPARISON_WORDING.DATE,
                    input: CONDITION_VALUE_INPUT.DATE,
                };
            case METADATA_TYPE.NUMERIC:
                return {
                    operators: CONDITION_OPERATORS,
                    wording: COMPARISON_WORDING.NUMBER,
                    input: CONDITION_VALUE_INPUT.NUMBER,
                };
            case METADATA_TYPE.TEXT:
                return {
                    operators: TEXT_CONDITION_OPERATORS,
                    wording: COMPARISON_WORDING.STATE,
                    input: CONDITION_VALUE_INPUT.TEXT,
                };
            default:
                return NO_COMPARISON;
        }
    }
}
